/* eslint-disable react-hooks/exhaustive-deps */
import Loader from 'components/atoms/loader';
import TextFilter from 'components/atoms/text_filter';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  getFinished,
  destroyManagedBatch,
  reactivateManagedBatch,
  getManagementMembers,
} from 'store/modules/management_store/actions';
import BatchConfirmModal from 'components/atoms/batch_confirm_modal';
import AffectedMembersSelect from 'components/atoms/affected_members_select';
import { showUndoToast } from 'store/modules/undo_toast_store/actions';
import { UndoToastState } from 'store/modules/undo_toast_store/types';
import createTrack from 'services/actions/tracker_actions';
import { ManagementState } from 'store/modules/management_store/types';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';
import ItemShare from 'components/atoms/item_share';
import SelectButton from 'components/atoms/select_button';
import Pagination from 'components/atoms/pagination';
import PerPageOptions from 'components/atoms/per_page_options';
import { getDownloadBiddings } from 'services/actions/bidding_actions';
import { toggleSelected } from 'services/toggleSelected';
import { useReactToPrint } from 'react-to-print';
import DataTable from 'react-data-table-component';
import useDebouncedValue from 'hooks/useDebouncedValue';
import FinishedFilter from '../finished_filter';
import FinishedList from '../finished_list';
import ItemTools from '../item_tools';
import { datatableColumns, datatableData } from '../finished_list/datatable_list';
import { Container } from './styles';

const DEFAULT_PER_PAGE = 20;

const NO_MATCH_MESSAGE = 'Nenhum item dos membros escolhidos está entre os selecionados.';

const FinishedPage = () => {
  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isCompanyView = management?.filtered_by === 'client';
  const canShowDeleteButton = isCompanyView ? !!profile.consultant_login : !!profile.alfa_tester;

  const dispatch = useDispatch();
  const mountedRef = useRef<boolean>(false);
  const [textFilterValue, setTextFilterValue] = useState<string>('');
  const debouncedText = useDebouncedValue(textFilterValue, 400);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const perPageOptions = [
    { value: 20, label: 20 },
    { value: 40, label: 40 },
    { value: 50, label: 50 },
    { value: 60, label: 60 },
    { value: 80, label: 80 },
    { value: 100, label: 100 },
  ];

  const handlePerPageChange = (value?: number) => {
    setPerPage(value || DEFAULT_PER_PAGE);
    setPage(1);
  };

  const [winnerChecked, setWinnerChecked] = useState<boolean>(true);
  const [partialWinnerChecked, setPartialWinnerChecked] = useState<boolean>(true);
  const [lostChecked, setLostChecked] = useState<boolean>(true);
  const [cancelledChecked, setCancelledChecked] = useState<boolean>(true);
  const [failedChecked, setFailedChecked] = useState<boolean>(true);
  const [declinedChecked, setDeclinedChecked] = useState<boolean>(true);
  const [revokedChecked, setRevokedChecked] = useState<boolean>(true);
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [selectedFinished, setSelectedFinished] = useState<Array<number>>([]);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState<boolean>(false);
  const [showBatchReactivateModal, setShowBatchReactivateModal] = useState<boolean>(false);
  const [batchRequestedCount, setBatchRequestedCount] = useState<number>(0);
  const [batchRemovedCount, setBatchRemovedCount] = useState<number | null>(null);
  const [batchReactivatedCount, setBatchReactivatedCount] = useState<number | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Array<number> | null>(null);
  const [batchErrorMessage, setBatchErrorMessage] = useState<string | undefined>(undefined);

  const { lastUndo } = useSelector<ApplicationState, UndoToastState>(
    (state) => state.session.undoToast,
  );
  const initialUndoAtRef = useRef<number | undefined>(lastUndo?.at);

  const statusFilter = useMemo<Array<string>>(() => {
    const out: Array<string> = [];
    if (winnerChecked) out.push('winner');
    if (partialWinnerChecked) out.push('partial_winner');
    if (lostChecked) out.push('lost');
    if (cancelledChecked) out.push('cancelled');
    if (failedChecked) out.push('failed');
    if (declinedChecked) out.push('declined');
    if (revokedChecked) out.push('revoked');
    return out;
  }, [
    winnerChecked,
    partialWinnerChecked,
    lostChecked,
    cancelledChecked,
    failedChecked,
    declinedChecked,
    revokedChecked,
  ]);

  const totalEntries = management.finished_total_entries || management.finished?.length || 0;

  const buildDeleteSuccessMessage = (requested: number, removed: number | null) => {
    const effectiveRemoved = removed ?? requested;
    if (effectiveRemoved <= 0) return 'Nenhuma licitação sua foi excluída.';
    const ownedLabel = isCompanyView
      ? effectiveRemoved === 1
        ? 'Licitação gerenciada pelos membros escolhidos foi excluída!'
        : `${effectiveRemoved} licitações gerenciadas pelos membros escolhidos foram excluídas!`
      : effectiveRemoved === 1
      ? 'Licitação finalizada por você foi excluída!'
      : `${effectiveRemoved} licitações finalizadas por você foram excluídas!`;
    if (removed !== null && removed < requested) {
      const skipped = requested - removed;
      const skippedLabel = isCompanyView
        ? skipped === 1
          ? 'não é gerenciada pelos membros escolhidos e foi mantida'
          : 'não são gerenciadas pelos membros escolhidos e foram mantidas'
        : skipped === 1
        ? 'também é gerenciada por outro membro e foi mantida'
        : 'também são gerenciadas por outros membros e foram mantidas';
      return `${ownedLabel} ${skipped} ${skippedLabel}.`;
    }
    return ownedLabel;
  };

  const buildReactivateSuccessMessage = (requested: number, reactivated: number | null) => {
    const effective = reactivated ?? requested;
    if (effective <= 0) return 'Nenhuma licitação sua foi reativada.';
    const ownedLabel =
      effective === 1
        ? 'Licitação reativada e voltou para Gerenciadas!'
        : `${effective} licitações reativadas e voltaram para Gerenciadas!`;
    if (reactivated !== null && reactivated < requested) {
      const skipped = requested - reactivated;
      const skippedLabel =
        skipped === 1
          ? 'pertence a outro membro e não foi reativada'
          : 'pertencem a outros membros e não foram reativadas';
      return `${ownedLabel} ${skipped} ${skippedLabel}.`;
    }
    return ownedLabel;
  };
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const downloadFile = (
    fileExtension: string,
    biddings_managed?: boolean,
    biddings_finished?: boolean,
  ) => {
    getDownloadBiddings(
      fileExtension,
      'licitacoes',
      selectedFinished,
      selectedFinished?.length,
      companyId(profile),
      biddings_managed,
      biddings_finished,
      false,
    );
  };

  const toggleSelectedFinished = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      selectedList: selectedFinished,
      setSelectedList: setSelectedFinished,
      sourceList: management?.finished,
      wholePageSelected,
      updateAll,
    });
  };
  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const handleTextFilter: Dispatch<SetStateAction<string>> = (value) => {
    setTextFilterValue(value);
    setPage(1);
  };

  const wrapStatusSetter =
    (setter: Dispatch<SetStateAction<boolean>>): Dispatch<SetStateAction<boolean>> =>
    (next) => {
      setter(next);
      setPage(1);
    };

  const fetchFinished = () => {
    dispatch(
      getFinished(management.filtered_by, companyId(profile), {
        page,
        per_page: perPage,
        q: debouncedText || undefined,
        status_in: statusFilter,
      }),
    );
  };

  const openBatchModal = (setModalOpened: Dispatch<SetStateAction<boolean>>) => {
    setSelectedMemberIds(null);
    setBatchErrorMessage(undefined);
    if (isCompanyView && management.members?.companyId !== companyId(profile)) {
      dispatch(getManagementMembers(companyId(profile)));
    }
    setModalOpened(true);
  };

  const managerMemberIds =
    management.members?.companyId === companyId(profile)
      ? [...management.members.active, ...management.members.inactive]
          .map((member) => member.id)
          .filter((id) =>
            (management?.finished || []).some(
              (bidding) =>
                selectedFinished.includes(bidding.id) &&
                (bidding.managers || []).some((manager) => manager.user_id === id),
            ),
          )
      : [];
  const affectedMemberIds = selectedMemberIds ?? managerMemberIds;

  const batchChoice = () => ({ memberIds: isCompanyView ? affectedMemberIds : undefined });

  const membersBlocked = isCompanyView && affectedMemberIds.length === 0;

  const membersSelect = isCompanyView ? (
    <AffectedMembersSelect
      active={management.members?.active || []}
      inactive={management.members?.inactive || []}
      value={affectedMemberIds}
      onChange={setSelectedMemberIds}
      currentUserId={profile.id}
      isLoading={!!request.get_management_members?.loading}
    />
  ) : null;

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    if (!windowDiv) return undefined;
    const handler = () => {
      setScrollToolsClass(windowDiv.scrollTop > 10 ? 'border-bottom' : '');
    };
    windowDiv.addEventListener('scroll', handler);
    return () => windowDiv.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    fetchFinished();
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedText,
    statusFilter,
    page,
    perPage,
    management.filtered_by,
    profile.currentCompany[tabId()],
  ]);

  useEffect(() => {
    if (!lastUndo || lastUndo.at === initialUndoAtRef.current) return;
    if (lastUndo.type === 'favorites') return;
    fetchFinished();
  }, [lastUndo?.at]);

  useEffect(() => {
    if (!management?.finished) return;
    setWholePageSelected(
      management.finished.length > 0 &&
        management.finished.every((bidding) => selectedFinished.includes(bidding.id)),
    );
  }, [selectedFinished, management?.finished]);

  const visibleList = management?.finished || [];
  const selectedBiddings = visibleList.filter((b) => selectedFinished.includes(b.id));
  const printColumns = datatableColumns(() => {}).filter((col) => col.name !== '');

  return (
    <div className="col-md-12">
      <div className="py-3 d-md-flex">
        <TextFilter textFilterValue={textFilterValue} setTextFilterValue={handleTextFilter} />
        <FinishedFilter
          filterWinner={winnerChecked}
          setFilterWinner={wrapStatusSetter(setWinnerChecked)}
          filterPartialWinner={partialWinnerChecked}
          setFilterPartialWinner={wrapStatusSetter(setPartialWinnerChecked)}
          filterLost={lostChecked}
          setFilterLost={wrapStatusSetter(setLostChecked)}
          filterCancelled={cancelledChecked}
          setFilterCancelled={wrapStatusSetter(setCancelledChecked)}
          filterFailed={failedChecked}
          setFilterFailed={wrapStatusSetter(setFailedChecked)}
          filterDeclined={declinedChecked}
          setFilterDeclined={wrapStatusSetter(setDeclinedChecked)}
          filterRevoked={revokedChecked}
          setFilterRevoked={wrapStatusSetter(setRevokedChecked)}
        />
      </div>
      <div>
        <Container>
          {request.finished.loading && <Loader />}
          <div className={`row list-tools py-1 ${scrollToolsClass}`}>
            <div className="position-relative col-md-6 d-block py-3 px-2 tour-remove-favorites-select-begin">
              <SelectButton
                isChecked={wholePageSelected}
                id={0}
                toggleSelected={toggleSelectedFinished}
                text="Selecionar página"
              />
              {selectedFinished?.length > 0 && (
                <div className="itemToolsSelected ml-3">
                  {selectedFinished?.length}{' '}
                  {selectedFinished?.length === 1
                    ? 'registro selecionado.'
                    : 'registros selecionados.'}
                  <button
                    type="button"
                    className="btn btn-link m-0 py-0 px-1 border-0"
                    onClick={() => setSelectedFinished([])}
                  >
                    Limpar seleção.
                  </button>
                </div>
              )}
            </div>
            <ItemTools
              enabledButtons={selectedFinished?.length > 0}
              biddings_finished
              disabledPopoverLabel="a licitação"
              downloadFile={downloadFile}
              handleShare={handleShare}
              handlePrint={handlePrint}
              showReactivateButton={canShowDeleteButton}
              handleReactivate={
                canShowDeleteButton ? () => setShowBatchReactivateModal(true) : undefined
              }
              showDeleteButton={canShowDeleteButton}
              handleDelete={
                canShowDeleteButton ? () => openBatchModal(setShowBatchDeleteModal) : undefined
              }
            />
            {showShareWindow && (
              <ItemShare setShowShareWindow={setShowShareWindow} selectedItems={selectedFinished} />
            )}
            {showBatchReactivateModal && (
              <BatchConfirmModal
                modalOpened={showBatchReactivateModal}
                setModalOpened={setShowBatchReactivateModal}
                title="Reativar licitações"
                message={
                  selectedFinished.length === 1
                    ? 'Tem certeza que deseja reativar esta licitação? Ela voltará para a aba Gerenciadas.'
                    : `Tem certeza que deseja reativar ${selectedFinished.length} licitações? Elas voltarão para a aba Gerenciadas.`
                }
                confirmLabel={
                  selectedFinished.length === 1
                    ? 'Reativar Licitação'
                    : `Reativar ${selectedFinished.length} Licitações`
                }
                successMessage={buildReactivateSuccessMessage(
                  batchRequestedCount,
                  batchReactivatedCount,
                )}
                errorMessage="Erro ao reativar as licitações."
                variant="primary"
                isLoading={request.finished.loading}
                isLoaded={request.finished.loaded}
                isError={request.finished.error}
                onConfirm={() => {
                  createTrack('Gestão de Licitações - Reativou licitações em lote');
                  setBatchRequestedCount(selectedFinished.length);
                  setBatchReactivatedCount(null);
                  const result: any = dispatch(
                    reactivateManagedBatch(
                      selectedFinished,
                      companyId(profile),
                      management?.filtered_by,
                    ),
                  );
                  if (result && typeof result.then === 'function') {
                    result.then((response: any) => {
                      if (!response || response.status >= 400) return;
                      const ids = response?.data?.reactivated_bidding_ids;
                      const undoToken = response?.data?.undo_token;
                      if (Array.isArray(ids)) setBatchReactivatedCount(ids.length);
                      if (Array.isArray(ids) && ids.length > 0 && undoToken) {
                        const message =
                          ids.length === 1
                            ? '1 licitação reativada.'
                            : `${ids.length} licitações reativadas.`;
                        dispatch(
                          showUndoToast({
                            type: 'reactivate',
                            ids,
                            undoToken,
                            message,
                            scope: management?.filtered_by,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchFinished();
                    });
                  }
                }}
                onSuccess={() => setSelectedFinished([])}
              />
            )}
            {showBatchDeleteModal && (
              <BatchConfirmModal
                modalOpened={showBatchDeleteModal}
                setModalOpened={setShowBatchDeleteModal}
                title="Excluir licitações"
                message={
                  selectedFinished.length === 1
                    ? 'Tem certeza que deseja excluir esta licitação finalizada? Ela não voltará para nenhuma lista.'
                    : `Tem certeza que deseja excluir ${selectedFinished.length} licitações finalizadas? Elas não voltarão para nenhuma lista.`
                }
                confirmLabel={
                  selectedFinished.length === 1
                    ? 'Excluir Licitação'
                    : `Excluir ${selectedFinished.length} Licitações`
                }
                successMessage={buildDeleteSuccessMessage(batchRequestedCount, batchRemovedCount)}
                errorMessage={batchErrorMessage || 'Erro ao excluir as licitações.'}
                variant="danger"
                isLoading={request.managed.loading}
                isLoaded={request.managed.loaded}
                isError={request.managed.error}
                confirmBlocked={membersBlocked}
                onConfirm={() => {
                  createTrack('Gestão de Licitações - Excluiu finalizadas em lote');
                  setBatchRequestedCount(selectedFinished.length);
                  setBatchRemovedCount(null);
                  setBatchErrorMessage(undefined);
                  setSelectedMemberIds(affectedMemberIds);
                  const result: any = dispatch(
                    destroyManagedBatch(
                      selectedFinished,
                      companyId(profile),
                      management?.filtered_by,
                      batchChoice(),
                    ),
                  );
                  if (result && typeof result.then === 'function') {
                    result.then((response: any) => {
                      if (response?.status === 404 && isCompanyView) {
                        setBatchErrorMessage(NO_MATCH_MESSAGE);
                      }
                      if (!response || response.status >= 400) return;
                      const ids = response?.data?.removed_bidding_ids;
                      const deletedAt = response?.data?.deleted_at;
                      if (Array.isArray(ids)) setBatchRemovedCount(ids.length);
                      if (Array.isArray(ids) && ids.length > 0 && deletedAt) {
                        const message =
                          ids.length === 1
                            ? '1 licitação removida.'
                            : `${ids.length} licitações removidas.`;
                        dispatch(
                          showUndoToast({
                            type: 'finished',
                            ids,
                            deletedAt,
                            message,
                            scope: management?.filtered_by,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchFinished();
                    });
                  }
                }}
                onSuccess={() => setSelectedFinished([])}
              >
                {membersSelect}
              </BatchConfirmModal>
            )}
            {request.finished.loaded && (
              <div className="row mb-3 px-4">
                <PerPageOptions
                  options={perPageOptions}
                  postsPerPage={perPage}
                  handleSelect={handlePerPageChange}
                />
              </div>
            )}
            {request.finished.loaded && (
              <FinishedList
                finishedList={visibleList}
                selectedFinished={selectedFinished}
                toggleSelected={toggleSelectedFinished}
              />
            )}
            {request.finished.loaded && totalEntries > perPage && (
              <div className="col-12 d-flex justify-content-end mt-4">
                <Pagination
                  postsPerPage={perPage}
                  totalPosts={totalEntries}
                  paginate={(p) => setPage(p)}
                  currentPage={page}
                  firstAndLastPage
                />
              </div>
            )}
          </div>
        </Container>
        <div style={{ display: 'none' }}>
          <div ref={componentRef}>
            <DataTable
              columns={printColumns}
              data={datatableData(selectedBiddings, companyId(profile), selectedFinished)}
              customStyles={{
                rows: { style: { fontSize: '12px' } },
                headCells: { style: { fontWeight: 'bold' } },
              }}
              pagination={false}
              selectableRows={false}
              dense
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinishedPage;
